import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, Trash2, Loader2, FileImage } from 'lucide-react';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/avif,image/heic,image/heif';
const MAX_BYTES = 5 * 1024 * 1024;

// Универсальный пикер обложки.
//
// Два режима:
//   • mode="staged" (для CREATE) — файл копится в локальном стейте, родителю
//     отдаётся через onPick. Загружать пока некуда — квиза ещё нет.
//   • mode="immediate" (для EDIT) — сразу зовёт onUpload(file) и ждёт isUploading.
//
// Превью: если есть локальный File — рендерим objectURL; иначе server URL.
const CoverPicker = ({
  mode = 'staged',
  serverUrl,
  pendingFile,
  onPick,
  onRemovePending,
  onUpload,
  onRemoveServer,
  isUploading = false,
  helper,
}) => {
  const inputRef = useRef(null);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);

  // objectURL — обязательно очищать, иначе утечка памяти.
  useEffect(() => {
    if (!pendingFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(pendingFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const showUrl = previewUrl || serverUrl || null;

  const handleFile = (file) => {
    setError('');
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      setError('Выберите файл изображения');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`Файл слишком большой (макс. ${Math.round(MAX_BYTES / 1024 / 1024)} MB)`);
      return;
    }
    if (mode === 'immediate') {
      onUpload?.(file);
    } else {
      onPick?.(file);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Обложка квиза</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Изображение автоматически конвертируется в WebP. До 5 MB.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-[200px_1fr] gap-4 items-start">
        <div className="relative aspect-video rounded-xl border border-dashed border-gray-300 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-800/50">
          {showUrl ? (
            <img src={showUrl} alt="Обложка" className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
              <FileImage className="w-8 h-8 mb-1" />
              <p className="text-xs">Нет обложки</p>
            </div>
          )}
          {isUploading && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <Loader2 className="w-7 h-7 text-white animate-spin" />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={isUploading}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ImagePlus className="w-4 h-4" />
            {showUrl ? 'Заменить' : 'Загрузить'}
          </button>

          {pendingFile && mode === 'staged' && (
            <button
              type="button"
              onClick={() => onRemovePending?.()}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-2 text-sm text-gray-700 dark:text-gray-200"
            >
              <Trash2 className="w-4 h-4" />
              Не загружать
            </button>
          )}

          {!pendingFile && serverUrl && mode === 'immediate' && (
            <button
              type="button"
              onClick={() => onRemoveServer?.()}
              disabled={isUploading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 dark:border-red-900/40 hover:bg-red-50 dark:hover:bg-red-950/20 text-red-600 dark:text-red-400 px-4 py-2 text-sm disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              Удалить обложку
            </button>
          )}

          {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
          {helper && <p className="text-xs text-gray-500 dark:text-gray-400">{helper}</p>}
        </div>
      </div>
    </div>
  );
};

export default CoverPicker;
